import { supabase } from '../supabase';
import { calendarService } from './calendarService';
import { travelService } from '../travelService';
import { format, addDays, parseISO } from 'date-fns';

export interface TravelCalendarEvent {
  id: string;
  trip_id: string;
  event_id: string;
  type: 'trip_start' | 'trip_end' | 'accommodation_checkin' | 'accommodation_checkout' | 'transportation_departure' | 'transportation_arrival' | 'custom';
  title: string;
  description?: string;
  location?: string;
  start_time: string;
  end_time: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface TravelCalendarIntegration {
  // Create calendar events for a trip
  createTripCalendarEvents(tripId: string): Promise<void>;
  
  // Create calendar events for accommodation
  createAccommodationCalendarEvents(accommodationId: string): Promise<void>;
  
  // Create calendar events for transportation
  createTransportationCalendarEvents(transportationId: string): Promise<void>;
  
  // Update calendar events when trip is modified
  updateTripCalendarEvents(tripId: string): Promise<void>;
  
  // Delete calendar events when trip is deleted
  deleteTripCalendarEvents(tripId: string): Promise<void>;
  
  // Get all calendar events for a trip
  getTripCalendarEvents(tripId: string): Promise<TravelCalendarEvent[]>;
  
  // Sync all travel data with calendar
  syncAllTravelWithCalendar(): Promise<void>;
  
  // Create custom calendar event for travel
  createCustomTravelEvent(event: Omit<TravelCalendarEvent, 'id' | 'created_at' | 'updated_at'>): Promise<TravelCalendarEvent>;
  
  // Delete custom travel calendar event
  deleteCustomTravelEvent(eventId: string): Promise<void>;
}

class TravelCalendarService implements TravelCalendarIntegration {
  private readonly TRIP_COLORS = {
    trip_start: '#10B981', // Green
    trip_end: '#EF4444', // Red
    accommodation_checkin: '#3B82F6', // Blue
    accommodation_checkout: '#8B5CF6', // Purple
    transportation_departure: '#F59E0B', // Orange
    transportation_arrival: '#06B6D4', // Cyan
    custom: '#6B7280' // Gray
  };

  async createTripCalendarEvents(tripId: string): Promise<void> {
    try {
      const trip = await travelService.trips.getById(tripId);
      if (!trip) throw new Error('Trip not found');

      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      // Create trip start event
      const startEvent = {
        title: `Trip Start: ${trip.title}`,
        description: `Starting trip to ${trip.destination || 'destination'}`,
        location: trip.destination,
        start_time: new Date(trip.start_date).toISOString(),
        end_time: new Date(trip.start_date).toISOString(),
        all_day: true,
        color: this.TRIP_COLORS.trip_start,
        module: 'travel',
        reminder_minutes: 60 * 24, // 24 hours before
        user_id: user.id
      };

      // Create trip end event
      const endEvent = {
        title: `Trip End: ${trip.title}`,
        description: `Ending trip to ${trip.destination || 'destination'}`,
        location: trip.destination,
        start_time: new Date(trip.end_date).toISOString(),
        end_time: new Date(trip.end_date).toISOString(),
        all_day: true,
        color: this.TRIP_COLORS.trip_end,
        module: 'travel',
        reminder_minutes: 60 * 24, // 24 hours before
        user_id: user.id
      };

      const [startEventResult, endEventResult] = await Promise.all([
        calendarService.createEvent(startEvent),
        calendarService.createEvent(endEvent)
      ]);

      // Store the mapping between trip and calendar events
      await this.storeTravelCalendarMapping(tripId, startEventResult.id, 'trip_start');
      await this.storeTravelCalendarMapping(tripId, endEventResult.id, 'trip_end');

    } catch (error) {
      console.error('Error creating trip calendar events:', error);
      throw error;
    }
  }

  async createAccommodationCalendarEvents(accommodationId: string): Promise<void> {
    try {
      const accommodation = await travelService.accommodations.getById(accommodationId);
      if (!accommodation) throw new Error('Accommodation not found');

      const trip = await travelService.trips.getById(accommodation.trip_id!);
      if (!trip) throw new Error('Trip not found');

      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      // Create check-in event
      const checkinEvent = {
        title: `Check-in: ${accommodation.name}`,
        description: `Checking into ${accommodation.type} at ${accommodation.address}`,
        location: accommodation.address,
        start_time: new Date(accommodation.check_in_date).toISOString(),
        end_time: new Date(accommodation.check_in_date).toISOString(),
        all_day: true,
        color: this.TRIP_COLORS.accommodation_checkin,
        module: 'travel',
        reminder_minutes: 60 * 2, // 2 hours before
        user_id: user.id
      };

      // Create check-out event
      const checkoutEvent = {
        title: `Check-out: ${accommodation.name}`,
        description: `Checking out from ${accommodation.type} at ${accommodation.address}`,
        location: accommodation.address,
        start_time: new Date(accommodation.check_out_date).toISOString(),
        end_time: new Date(accommodation.check_out_date).toISOString(),
        all_day: true,
        color: this.TRIP_COLORS.accommodation_checkout,
        module: 'travel',
        reminder_minutes: 60 * 2, // 2 hours before
        user_id: user.id
      };

      const [checkinEventResult, checkoutEventResult] = await Promise.all([
        calendarService.createEvent(checkinEvent),
        calendarService.createEvent(checkoutEvent)
      ]);

      // Store the mapping
      await this.storeTravelCalendarMapping(accommodation.trip_id!, checkinEventResult.id, 'accommodation_checkin');
      await this.storeTravelCalendarMapping(accommodation.trip_id!, checkoutEventResult.id, 'accommodation_checkout');

    } catch (error) {
      console.error('Error creating accommodation calendar events:', error);
      throw error;
    }
  }

  async createTransportationCalendarEvents(transportationId: string): Promise<void> {
    try {
      const transportation = await travelService.transportation.getById(transportationId);
      if (!transportation) throw new Error('Transportation not found');

      const trip = await travelService.trips.getById(transportation.trip_id!);
      if (!trip) throw new Error('Trip not found');

      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      // Create departure event
      const departureEvent = {
        title: `${transportation.type.toUpperCase()}: ${transportation.provider} - Departure`,
        description: `Departing from ${transportation.departure_location} to ${transportation.arrival_location}`,
        location: transportation.departure_location,
        start_time: transportation.departure_time,
        end_time: new Date(new Date(transportation.departure_time).getTime() + 30 * 60 * 1000).toISOString(), // 30 min duration
        all_day: false,
        color: this.TRIP_COLORS.transportation_departure,
        module: 'travel',
        reminder_minutes: 60, // 1 hour before
        user_id: user.id
      };

      // Create arrival event
      const arrivalEvent = {
        title: `${transportation.type.toUpperCase()}: ${transportation.provider} - Arrival`,
        description: `Arriving at ${transportation.arrival_location} from ${transportation.departure_location}`,
        location: transportation.arrival_location,
        start_time: transportation.arrival_time,
        end_time: new Date(new Date(transportation.arrival_time).getTime() + 30 * 60 * 1000).toISOString(), // 30 min duration
        all_day: false,
        color: this.TRIP_COLORS.transportation_arrival,
        module: 'travel',
        reminder_minutes: 30, // 30 minutes before
        user_id: user.id
      };

      const [departureEventResult, arrivalEventResult] = await Promise.all([
        calendarService.createEvent(departureEvent),
        calendarService.createEvent(arrivalEvent)
      ]);

      // Store the mapping
      await this.storeTravelCalendarMapping(transportation.trip_id!, departureEventResult.id, 'transportation_departure');
      await this.storeTravelCalendarMapping(transportation.trip_id!, arrivalEventResult.id, 'transportation_arrival');

    } catch (error) {
      console.error('Error creating transportation calendar events:', error);
      throw error;
    }
  }

  async updateTripCalendarEvents(tripId: string): Promise<void> {
    try {
      // First delete existing events
      await this.deleteTripCalendarEvents(tripId);
      
      // Then create new events
      await this.createTripCalendarEvents(tripId);
      
      // Update accommodation events
      const accommodations = await travelService.accommodations.getAll(tripId);
      for (const accommodation of accommodations) {
        await this.createAccommodationCalendarEvents(accommodation.id!);
      }
      
      // Update transportation events
      const transportation = await travelService.transportation.getAll(tripId);
      for (const transport of transportation) {
        await this.createTransportationCalendarEvents(transport.id!);
      }
      
    } catch (error) {
      console.error('Error updating trip calendar events:', error);
      throw error;
    }
  }

  async deleteTripCalendarEvents(tripId: string): Promise<void> {
    try {
      // Get all calendar event mappings for this trip
      const { data: mappings, error } = await supabase
        .from('travel_calendar_events')
        .select('event_id')
        .eq('trip_id', tripId);

      if (error) throw error;

      // Delete all associated calendar events
      for (const mapping of mappings || []) {
        await calendarService.deleteEvent(mapping.event_id);
      }

      // Delete the mappings
      await supabase
        .from('travel_calendar_events')
        .delete()
        .eq('trip_id', tripId);

    } catch (error) {
      console.error('Error deleting trip calendar events:', error);
      throw error;
    }
  }

  async getTripCalendarEvents(tripId: string): Promise<TravelCalendarEvent[]> {
    try {
      const { data, error } = await supabase
        .from('travel_calendar_events')
        .select(`
          *,
          calendar_events (
            id,
            title,
            description,
            location,
            start_time,
            end_time,
            color
          )
        `)
        .eq('trip_id', tripId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return (data || []).map(item => ({
        id: item.id,
        trip_id: item.trip_id,
        event_id: item.event_id,
        type: item.type,
        title: item.calendar_events.title,
        description: item.calendar_events.description,
        location: item.calendar_events.location,
        start_time: item.calendar_events.start_time,
        end_time: item.calendar_events.end_time,
        color: item.calendar_events.color,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));

    } catch (error) {
      console.error('Error getting trip calendar events:', error);
      throw error;
    }
  }

  async syncAllTravelWithCalendar(): Promise<void> {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      // Get all trips for the user
      const trips = await travelService.trips.getAll();
      
      for (const trip of trips) {
        // Check if calendar events already exist for this trip
        const existingEvents = await this.getTripCalendarEvents(trip.id!);
        
        if (existingEvents.length === 0) {
          // Create calendar events for this trip
          await this.createTripCalendarEvents(trip.id!);
          
          // Create events for accommodations
          const accommodations = await travelService.accommodations.getAll(trip.id!);
          for (const accommodation of accommodations) {
            await this.createAccommodationCalendarEvents(accommodation.id!);
          }
          
          // Create events for transportation
          const transportation = await travelService.transportation.getAll(trip.id!);
          for (const transport of transportation) {
            await this.createTransportationCalendarEvents(transport.id!);
          }
        }
      }
      
    } catch (error) {
      console.error('Error syncing travel with calendar:', error);
      throw error;
    }
  }

  async createCustomTravelEvent(event: Omit<TravelCalendarEvent, 'id' | 'created_at' | 'updated_at'>): Promise<TravelCalendarEvent> {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      // Create the calendar event
      const calendarEvent = await calendarService.createEvent({
        title: event.title,
        description: event.description,
        location: event.location,
        start_time: event.start_time,
        end_time: event.end_time,
        all_day: false,
        color: event.color || this.TRIP_COLORS.custom,
        module: 'travel',
        user_id: user.id
      });

      // Store the mapping
      const mapping = await this.storeTravelCalendarMapping(event.trip_id, calendarEvent.id, event.type);

      return {
        id: mapping.id,
        trip_id: event.trip_id,
        event_id: calendarEvent.id,
        type: event.type,
        title: event.title,
        description: event.description,
        location: event.location,
        start_time: event.start_time,
        end_time: event.end_time,
        color: event.color || this.TRIP_COLORS.custom,
        created_at: mapping.created_at,
        updated_at: mapping.updated_at
      };

    } catch (error) {
      console.error('Error creating custom travel event:', error);
      throw error;
    }
  }

  async deleteCustomTravelEvent(eventId: string): Promise<void> {
    try {
      // Get the mapping
      const { data: mapping, error } = await supabase
        .from('travel_calendar_events')
        .select('event_id')
        .eq('id', eventId)
        .single();

      if (error) throw error;

      // Delete the calendar event
      await calendarService.deleteEvent(mapping.event_id);

      // Delete the mapping
      await supabase
        .from('travel_calendar_events')
        .delete()
        .eq('id', eventId);

    } catch (error) {
      console.error('Error deleting custom travel event:', error);
      throw error;
    }
  }

  private async storeTravelCalendarMapping(tripId: string, eventId: string, type: string): Promise<any> {
    const { data, error } = await supabase
      .from('travel_calendar_events')
      .insert([{
        trip_id: tripId,
        event_id: eventId,
        type: type,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Helper method to get upcoming travel events
  async getUpcomingTravelEvents(days: number = 30): Promise<TravelCalendarEvent[]> {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      const startDate = new Date();
      const endDate = addDays(startDate, days);

      const { data, error } = await supabase
        .from('travel_calendar_events')
        .select(`
          *,
          calendar_events (
            id,
            title,
            description,
            location,
            start_time,
            end_time,
            color
          )
        `)
        .gte('calendar_events.start_time', startDate.toISOString())
        .lte('calendar_events.start_time', endDate.toISOString())
        .order('calendar_events.start_time', { ascending: true });

      if (error) throw error;

      return (data || []).map(item => ({
        id: item.id,
        trip_id: item.trip_id,
        event_id: item.event_id,
        type: item.type,
        title: item.calendar_events.title,
        description: item.calendar_events.description,
        location: item.calendar_events.location,
        start_time: item.calendar_events.start_time,
        end_time: item.calendar_events.end_time,
        color: item.calendar_events.color,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));

    } catch (error) {
      console.error('Error getting upcoming travel events:', error);
      throw error;
    }
  }
}

export const travelCalendarService = new TravelCalendarService();
